import { Button } from '@repo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card'
import { Field, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	postBackofficeAuthGetSession,
	postBackofficeAuthGeneratePasskeyStepUpOptions,
	postBackofficeAuthSendTwoFactorOtp,
	postBackofficeAuthVerifyBackupCode,
	postBackofficeAuthVerifyTotp,
	postBackofficeAuthVerifyTwoFactorOtp,
	postBackofficeAuthVerifyTwoFactorPasskey
} from '@repo/sdk/gen'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { getPasskeyAssertion } from '@repo/ui/modules/auth/lib/webauthn'
import { useTranslation } from '@/lib/i18n'

export const Route = createFileRoute('/mfa')({
	ssr: false,
	loader: async ({ context }) => {
		const response = await postBackofficeAuthGetSession()
		context.queryClient.setQueryData(['auth', 'session'], response.data)
		if (response.data.sessionType === 'authenticated') {
			throw redirect({ to: '/' })
		}
		if (response.data.sessionType !== 'mfa_pending') {
			throw redirect({ to: '/login' })
		}
		if (!response.data.mfa) {
			throw redirect({ to: '/login' })
		}
		return response.data.mfa
	},
	component: MfaPage
})

function MfaPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const challenge = Route.useLoaderData()
	const [method, setMethod] = useState(
		challenge.methods[0]?.type ?? 'passkey'
	)
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const totpMethod = challenge.methods.find(
		(candidate) => candidate.type === 'totp'
	)
	const hasEmailOtp = challenge.methods.some(
		(candidate) => candidate.type === 'email_otp'
	)
	const hasRecoveryCode = challenge.methods.some(
		(candidate) => candidate.type === 'recovery_code'
	)
	const hasPasskey = challenge.methods.some(
		(candidate) => candidate.type === 'passkey'
	)

	async function verify() {
		setError(null)
		setIsSubmitting(true)
		try {
			if (method === 'totp' && totpMethod?.type === 'totp') {
				await postBackofficeAuthVerifyTotp({
					body: {
						code,
						deviceId: totpMethod.deviceId,
						mfaToken: challenge.mfaToken
					}
				})
			} else if (method === 'email_otp') {
				await postBackofficeAuthVerifyTwoFactorOtp({
					body: { code, mfaToken: challenge.mfaToken }
				})
			} else if (method === 'recovery_code') {
				await postBackofficeAuthVerifyBackupCode({
					body: { code, mfaToken: challenge.mfaToken }
				})
			} else {
				setError(t('common:mfa_unavailable'))
				return
			}
			await navigate({ to: '/' })
		} catch {
			setError(t('common:mfa_error'))
		} finally {
			setIsSubmitting(false)
		}
	}

	async function sendEmailCode() {
		setError(null)
		setIsSubmitting(true)
		try {
			await postBackofficeAuthSendTwoFactorOtp({
				body: { mfaToken: challenge.mfaToken }
			})
			setMethod('email_otp')
		} catch {
			setError(t('common:mfa_error'))
		} finally {
			setIsSubmitting(false)
		}
	}

	async function verifyPasskey() {
		setError(null)
		setIsSubmitting(true)
		try {
			const passkeyChallenge =
				await postBackofficeAuthGeneratePasskeyStepUpOptions({
					body: {}
				})
			const response = await getPasskeyAssertion(
				passkeyChallenge.data.options
			)
			await postBackofficeAuthVerifyTwoFactorPasskey({
				body: {
					mfaToken: challenge.mfaToken,
					passkeyToken: passkeyChallenge.data.passkeyToken,
					response
				}
			})
			await navigate({ to: '/' })
		} catch {
			setError(t('common:mfa_error'))
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<main className='grid min-h-screen place-items-center bg-background p-5'>
			<Card className='w-full max-w-md rounded-2xl'>
				<CardHeader>
					<CardTitle>{t('common:mfa_title')}</CardTitle>
					<p className='text-muted-foreground text-sm'>
						{t('common:mfa_copy')}
					</p>
				</CardHeader>
				<CardContent className='space-y-5'>
					<div className='grid gap-2 sm:grid-cols-2'>
						{totpMethod ? (
							<Button
								onClick={() => setMethod('totp')}
								variant={
									method === 'totp' ? 'default' : 'outline'
								}
							>
								{t('common:mfa_totp')}
							</Button>
						) : null}
						{hasEmailOtp ? (
							<Button
								onClick={() => setMethod('email_otp')}
								variant={
									method === 'email_otp'
										? 'default'
										: 'outline'
								}
							>
								{t('common:mfa_email')}
							</Button>
						) : null}
						{hasRecoveryCode ? (
							<Button
								onClick={() => setMethod('recovery_code')}
								variant={
									method === 'recovery_code'
										? 'default'
										: 'outline'
								}
							>
								{t('common:mfa_recovery')}
							</Button>
						) : null}
						{hasPasskey ? (
							<Button
								onClick={() => setMethod('passkey')}
								variant={
									method === 'passkey' ? 'default' : 'outline'
								}
							>
								{t('common:mfa_passkey')}
							</Button>
						) : null}
					</div>
					{method === 'email_otp' ? (
						<Button
							disabled={isSubmitting}
							onClick={sendEmailCode}
							variant='outline'
						>
							{t('common:mfa_send_email')}
						</Button>
					) : null}
					{method !== 'passkey' ? (
						<Field>
							<FieldLabel htmlFor='mfa-code'>
								{t('common:mfa_code')}
							</FieldLabel>
							<Input
								autoComplete='one-time-code'
								id='mfa-code'
								onChange={(event) =>
									setCode(event.target.value)
								}
								value={code}
							/>
						</Field>
					) : null}
					{error ? (
						<p className='text-destructive text-sm'>{error}</p>
					) : null}
					<Button
						className='w-full'
						disabled={
							isSubmitting || (method !== 'passkey' && !code)
						}
						onClick={method === 'passkey' ? verifyPasskey : verify}
					>
						{method === 'passkey'
							? t('common:mfa_use_passkey')
							: t('common:mfa_verify')}
					</Button>
				</CardContent>
			</Card>
		</main>
	)
}
